<template>
  <div class="flex flex-col gap-4" data-testid="activity-requests-tab">
    <!-- Visões salvas (Q14) + ordenação + exportar -->
    <div class="flex flex-wrap items-center gap-2" data-testid="activity-requests-saved-views">
      <span class="text-xs font-bold text-neutral50">Visões salvas</span>
      <span
        v-for="view in savedViews.views.value"
        :key="view.id"
        class="inline-flex items-center gap-1 rounded-full border border-lightGray30 bg-white px-2 py-0.5 text-xs"
      >
        <button
          type="button"
          :data-testid="`activity-requests-saved-view-${view.id}`"
          @click="applySavedView(view)"
        >
          <span v-if="view.isDefault" title="Padrão ao abrir a aba">★ </span>{{ view.name }}
        </button>
        <button
          type="button"
          class="text-neutral50"
          :aria-label="`Remover a visão ${view.name}`"
          :data-testid="`activity-requests-saved-view-${view.id}-remove`"
          @click="savedViews.removeView(view.id)"
        >
          ×
        </button>
      </span>
      <button
        type="button"
        class="text-xs font-bold text-primary30 underline"
        data-testid="activity-requests-save-view-button"
        @click="isSaveFormOpen = !isSaveFormOpen"
      >
        + Salvar visão
      </button>

      <div class="ml-auto flex items-center gap-2">
        <SelectFilter
          label="Ordenar por"
          :is-open="isSortOpen"
          :options="SORT_OPTIONS"
          :selected-value="filters.orderBy"
          dropdown-width="w-64"
          align-right
          data-testid="activity-requests-sort"
          @toggle="isSortOpen = !isSortOpen"
          @select="onSort"
        />
        <div v-if="canExport" data-testid="activity-requests-export-button">
          <OutlinedButton
            text="Exportar"
            width="auto"
            height="32px"
            @click="isExportConfirmOpen = true"
          />
        </div>
      </div>

      <form
        v-if="isSaveFormOpen"
        class="flex w-full flex-wrap items-center gap-2 rounded-lg border border-lightGray30 bg-white p-2"
        data-testid="activity-requests-save-view-form"
        @submit.prevent="submitSaveView"
      >
        <input
          v-model="newViewName"
          :maxlength="SAVED_VIEW_NAME_MAX_LENGTH"
          placeholder="Nome da visão"
          class="h-8 rounded-md border border-lightGray30 px-2 text-sm"
          data-testid="activity-requests-save-view-name-input"
        />
        <label class="flex items-center gap-1 text-xs">
          <input
            v-model="newViewIsDefault"
            type="checkbox"
            data-testid="activity-requests-save-view-default-checkbox"
          />
          Abrir esta visão por padrão nesta aba
        </label>
        <span class="text-[11px] text-neutral50">
          Salva unidades, visão, filtros e ordenação. Não salva a busca.
        </span>
        <button
          type="submit"
          class="rounded-md bg-primary30 px-3 py-1 text-xs font-bold text-white"
          data-testid="activity-requests-save-view-submit"
        >
          Salvar
        </button>
      </form>
    </div>

    <ActivityRequestQuickViews
      :counters="counters"
      :active="activeQuickView"
      :loading="loading && counters === null"
      @select="onQuickView"
    />

    <ActivityRequestFilterBar
      :filters="filters"
      :unit-options="unitOptions"
      :schedule-options="scheduleOptions"
      :shift-options="shiftOptions"
      :group-options="groupOptions"
      :shift-options-unavailable="shiftOptionsUnavailable"
      @update="update"
      @clear="clear"
    />

    <template v-if="loading">
      <div v-for="n in 3" :key="n" class="mb-4 h-40 animate-pulse rounded-lg bg-lightGray30" />
    </template>
    <ReportErrorState
      v-else-if="loadError"
      title="Não foi possível carregar as solicitações"
      :message="loadError"
      @retry="load()"
    />
    <template v-else-if="items.length">
      <ActivityRequestCard
        v-for="item in items"
        :key="item.id"
        :item="item"
        :now-ms="nowMs"
        @details="openDrawerById"
        @send-record="openRecord"
      />
      <v-pagination
        v-if="pageCount > 1"
        :model-value="page"
        :length="pageCount"
        :total-visible="5"
        density="comfortable"
        color="primary"
        @update:model-value="setPage"
      />
    </template>
    <div
      v-else
      class="flex flex-col items-center gap-2 py-10 text-center"
      data-testid="activity-requests-empty"
    >
      <p class="m-0 text-md font-bold text-textPrimary">{{ emptyTitle }}</p>
      <button
        v-if="hasActive"
        type="button"
        class="text-sm font-bold text-primary30 underline"
        data-testid="activity-requests-empty-clear"
        @click="clear()"
      >
        Limpar filtros
      </button>
    </div>

    <Modal :modal-active="isExportConfirmOpen" @close="isExportConfirmOpen = false">
      <template #header>
        <p class="font-bold">Exportar solicitações</p>
      </template>
      <p class="text-sm" data-testid="activity-requests-export-confirm">
        {{
          count === 1
            ? '1 linha será exportada'
            : `${count.toLocaleString('pt-BR')} linhas serão exportadas`
        }}
        com os filtros atuais (acima de 20.000 o servidor recusa — refine o recorte).
      </p>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <button
            type="button"
            class="text-sm"
            data-testid="activity-requests-export-cancel"
            @click="isExportConfirmOpen = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="rounded-md bg-primary30 px-3 py-1 text-sm font-bold text-white disabled:opacity-50"
            :disabled="isExporting || count === 0"
            data-testid="activity-requests-export-submit"
            @click="confirmExport"
          >
            Exportar
          </button>
        </div>
      </template>
    </Modal>

    <ActivityRequestDetailDrawer
      v-model="isDrawerOpen"
      :item="drawerItem"
      :now-ms="nowMs"
      @send-record="openRecord"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import { useServerNowTick } from '@/components/designSystem/QueueViews/useServerNowTick'
import type { ModuleViewCounters } from '@/components/designSystem/QueueViews/types'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  ACTIVITY_REQUEST_ORDER_BY,
  ACTIVITY_REQUEST_ORDER_BY_LABELS,
  ACTIVITY_REQUEST_PAGE_SIZE
} from '@/constants/activity-requests.constants'
import type { ShiftResponse } from '@/Network/Types/Responses/ShiftResponses'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import type {
  ActivityRequestItem,
  ActivityRequestOrderBy,
  ActivityRequestQuery,
  ActivityRequestQuickViewKey
} from '@/types/activity-requests.types'
import { getApiErrorMessage } from '@/utils/apiError'

import ActivityRequestCard from './ActivityRequestCard.vue'
import ActivityRequestDetailDrawer from './ActivityRequestDetailDrawer.vue'
import ActivityRequestFilterBar from './ActivityRequestFilterBar.vue'
import ActivityRequestQuickViews from './ActivityRequestQuickViews.vue'
import {
  applyQuickView,
  hasActiveActivityRequestFilters,
  quickViewOf,
  useActivityRequestFilters
} from '../../composables/useActivityRequestFilters'
import {
  useActivityRequestSavedViews,
  SAVED_VIEW_NAME_MAX_LENGTH
} from '../../composables/useActivityRequestSavedViews'
import type { ActivityRequestSavedView } from '../../composables/useActivityRequestSavedViews'
import { useOpenAttendanceContainer } from '../../composables/useOpenAttendanceContainer'
import {
  buildScheduleOptions,
  buildShiftOptions,
  buildUnitOptions
} from '../../utils/activity-request-filter-options'
import type { FilterOption } from '../../utils/activity-request-filter-options'

defineProps<{ canExport: boolean }>()
const emit = defineEmits<{ counters: [counters: ModuleViewCounters] }>()

const toast = useToast()
const { notifyError } = useErrorToast()
const dsAttendanceStore = useDsAttendanceStore()
const { openContainer } = useOpenAttendanceContainer()

/* ------------------------------------------------------------ filtros e visões */

const savedViews = useActivityRequestSavedViews()
const { filters, page, query, update, setPage, clear, replaceAll, hydrate } =
  useActivityRequestFilters({ initial: () => savedViews.defaultView.value?.query ?? null })

// Hidrata ANTES de observar a query: a primeira carga é a do `onMounted`, não um disparo do watch.
hydrate()

const activeQuickView = computed(() => quickViewOf(filters.value))
const hasActive = computed(() => hasActiveActivityRequestFilters(filters.value))

const SORT_OPTIONS = ACTIVITY_REQUEST_ORDER_BY.map((value) => ({
  label: ACTIVITY_REQUEST_ORDER_BY_LABELS[value],
  value
}))
const isSortOpen = ref(false)

function onSort(value: string): void {
  isSortOpen.value = false
  update({ orderBy: value as ActivityRequestOrderBy }, { keepPage: false })
}

function onQuickView(key: ActivityRequestQuickViewKey): void {
  update(applyQuickView(filters.value, key))
}

const isSaveFormOpen = ref(false)
const newViewName = ref('')
const newViewIsDefault = ref(false)

function submitSaveView(): void {
  const saved = savedViews.saveView(newViewName.value, filters.value, newViewIsDefault.value)
  if (!saved) {
    toast.error('Dê um nome à visão.')
    return
  }
  toast.success(`Visão "${saved.name}" salva.`)
  isSaveFormOpen.value = false
  newViewName.value = ''
  newViewIsDefault.value = false
}

function applySavedView(view: ActivityRequestSavedView): void {
  replaceAll(savedViews.filtersOf(view))
}

/* ------------------------------------------------------------ opções dos filtros */

const groupOptions = ref<FilterOption[]>([])
const shifts = ref<ShiftResponse[]>([])
const shiftOptionsUnavailable = ref(false)

const unitOptions = computed(() => buildUnitOptions(dsAttendanceStore.filterHealthUnits))
const scheduleOptions = computed(() =>
  buildScheduleOptions(
    dsAttendanceStore.filterSchedules,
    dsAttendanceStore.filterHealthUnits,
    filters.value.healthUnitIds,
    filters.value.scheduleIds
  )
)
const shiftOptions = computed(() =>
  buildShiftOptions(
    shifts.value,
    dsAttendanceStore.filterSchedules,
    dsAttendanceStore.filterHealthUnits,
    filters.value.healthUnitIds,
    filters.value.scheduleIds,
    filters.value.shiftIds
  )
)

/** Cargas independentes: uma falha não impede as outras (padrão de Pagamentos prioritários). */
async function loadOptions(): Promise<void> {
  await Promise.all([
    dsAttendanceStore.filterHealthUnits.length
      ? Promise.resolve()
      : dsAttendanceStore.fetchFilterOptions(),
    new FieldOptionsService()
      .getOperationalGroups()
      .then((groups) => {
        groupOptions.value = groups.map((group) => ({ label: group.name, value: group.name }))
      })
      .catch((error: unknown) =>
        notifyError(error, 'Não foi possível carregar os grupos operacionais')
      ),
    // `GET /shifts` pode responder 403 ao receptor puro: o filtro de Turno avisa e segue.
    Network.shift
      .list()
      .then((response) => {
        shifts.value = response.data ?? []
      })
      .catch(() => {
        shiftOptionsUnavailable.value = true
      })
  ])
}

/* ------------------------------------------------------------ lista e contadores */

const items = ref<ActivityRequestItem[]>([])
const count = ref(0)
const counters = ref<ModuleViewCounters | null>(null)
const serverNow = ref<string | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)

const { nowMs } = useServerNowTick(() => serverNow.value)

const pageCount = computed(() => Math.max(1, Math.ceil(count.value / ACTIVITY_REQUEST_PAGE_SIZE)))

let loadSeq = 0

/**
 * "Reabertas" é quick view (`view=ALL&reopened=true`), não filtro: como as outras, ela não
 * pode recortar os contadores — senão, ao clicá-la, Vencidas/No prazo/Todas passariam a
 * contar só as reabertas (M1-B, balão 1: o número respeita os DEMAIS filtros).
 */
function countersQueryOf(current: ActivityRequestQuery): ActivityRequestQuery {
  return activeQuickView.value === 'REOPENED' ? { ...current, reopened: null } : current
}

async function load(): Promise<void> {
  const seq = ++loadSeq
  const current = query.value
  loading.value = true
  loadError.value = null
  try {
    const [list, countersResponse] = await Promise.all([
      Network.dsAttendance.listActivityRequests(current),
      // Contador é conveniência: a falha dele apaga os números ("—"), nunca a lista.
      Network.dsAttendance.getActivityRequestCounters(countersQueryOf(current)).catch(() => null)
    ])
    if (seq !== loadSeq) return
    items.value = list.items
    count.value = list.count
    serverNow.value = list.serverNow
    counters.value = countersResponse
    if (countersResponse) emit('counters', countersResponse)
    openDrawerFromSearch()
  } catch (error) {
    if (seq !== loadSeq) return
    items.value = []
    count.value = 0
    loadError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar as solicitações de Registro de Atividade.'
    )
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

watch(query, () => void load(), { deep: true })

/* ------------------------------------------------------------ gaveta e navegação */

const isDrawerOpen = ref(false)
const drawerItem = ref<ActivityRequestItem | null>(null)

function openDrawerById(entityId: number): void {
  const item = items.value.find((candidate) => candidate.entityId === entityId)
  if (!item) return
  drawerItem.value = item
  isDrawerOpen.value = true
}

/** `#1287` na busca abre a gaveta direto (M1-B, balão 1). */
function openDrawerFromSearch(): void {
  const match = /^#(\d+)$/.exec(filters.value.search.trim())
  if (match) openDrawerById(Number(match[1]))
}

function openRecord(entityId: number): void {
  const item = items.value.find((candidate) => candidate.entityId === entityId) ?? drawerItem.value
  if (!item) return
  void openContainer(entityId, item.payload.status)
}

/* ------------------------------------------------------------ export */

const isExportConfirmOpen = ref(false)
const isExporting = ref(false)

async function confirmExport(): Promise<void> {
  isExporting.value = true
  try {
    await Network.dsAttendance.exportActivityRequests(query.value)
    toast.success('Exportação concluída.')
    isExportConfirmOpen.value = false
  } catch (error) {
    notifyError(error, 'Não foi possível exportar as solicitações.')
  } finally {
    isExporting.value = false
  }
}

/* ------------------------------------------------------------ estado vazio */

const emptyTitle = computed(() => {
  if (hasActive.value) return 'Nenhum resultado para estes filtros'
  if (filters.value.view === 'OVERDUE') return 'Nenhuma solicitação vencida — tudo em dia'
  return 'Nenhuma solicitação neste recorte'
})

onMounted(() => {
  void loadOptions()
  void load()
})
</script>
